import React, { useEffect } from 'react';
import { Tabs, Tab } from 'carbon-components-react';
import ReferralContactModal from './ReferralContactModal';
import ReferralDetails from './ReferralDetails';
import ReferralSafetyMessage from './ReferralSafetyMessage';
import ReferralResponseList from './ReferralResponseList';
import ReferralResponseNotReady from './ReferralResponseNotReady';
import ReferralResponseExpired from './ReferralResponseExpired';
import ReferralTabLabel from './ReferralTabLabel';
import {
  ResponseBodyModal,
  ResponseWebMapModal,
  ResponsePdfModal,
  ResponseImageModal
} from './ResponseModals';
import ReferralAnnouncement from './ReferralAnnouncement';
import ResponsesExpiredModal from '../../ResponsesExpiredModal';

type Props = {
  referrals: any;
  enquiryId: number;
  responsesExpired: boolean;
  showResponsesExpiredModal: boolean;
  selectedReferral: number;
  selectedReferralObj: any;
  selectedResponse: number;
  loadingEmailPreview: any;
  emailPreview: any;
  viewOrDownloadDropdownItem: Array<any>;
  handleViewOrDownloadDropdownChange: Function;
  previewData: any;
  inboundEmailIdToDownload: number;
  handleResponseSelectionMobile: Function;
  handleMultipleResponseSelection: Function;
  onClickCloseResponseExpiredModal: Function;
};

const MobileViewGetPlans: React.FC<Props> = ({
  referrals,
  enquiryId,
  responsesExpired,
  showResponsesExpiredModal,
  selectedReferral,
  selectedReferralObj,
  selectedResponse,
  loadingEmailPreview,
  emailPreview,
  viewOrDownloadDropdownItem,
  handleViewOrDownloadDropdownChange,
  previewData,
  inboundEmailIdToDownload,
  handleResponseSelectionMobile,
  handleMultipleResponseSelection,
  onClickCloseResponseExpiredModal
}) => {
  const [
    showReferralContactModal,
    setShowReferralContactModal
  ] = React.useState<boolean>(false);
  const [
    showResponseBodyModal,
    setShowResponseBodyModal
  ] = React.useState<boolean>(false);
  const [
    showResponseWebMapModal,
    setShowResponseWebMapModal
  ] = React.useState<boolean>(false);
  const [
    showResponsePdfModal,
    setShowResponsePdfModal
  ] = React.useState<boolean>(false);
  const [
    showResponseImageModal,
    setShowResponseImageModal
  ] = React.useState<boolean>(false);

  const handleShowReferralContactModal = () => {
    setShowReferralContactModal((prev) => !prev);
  };

  // Create sorted referrals array with original indices preserved
  const sortedReferralsWithIndex = React.useMemo(() => {
    if (!referrals?.length) return [];

    return referrals
      .map((referral: any, originalIndex: number) => {
        const status = responsesExpired
          ? 'EXPIRED'
          : referral.status === 'COMPLETED'
          ? 'COMPLETED'
          : 'WAITING';

        return {
          ...referral,
          originalIndex,
          status
        };
      })
      .sort((a: any, b: any) => {
        // Sort COMPLETED referrals first
        if (a.status === 'COMPLETED' && b.status !== 'COMPLETED') return -1;
        if (a.status !== 'COMPLETED' && b.status === 'COMPLETED') return 1;
        return 0; // Maintain original order for same status
      });
  }, [referrals, responsesExpired]);

  // Map selected index from original to sorted display order
  const selectedDisplayIndex = React.useMemo(() => {
    if (selectedReferral === -1) return -1;
    return sortedReferralsWithIndex.findIndex(
      (item: any) => item.originalIndex === selectedReferral
    );
  }, [selectedReferral, sortedReferralsWithIndex]);

  // Auto select the first referral on page load if none is selected
  useEffect(() => {
    if (selectedReferral === -1 && sortedReferralsWithIndex?.length > 0) {
      // Select the first referral in the sorted display order (which might not be original index 0)
      const firstReferralOriginalIndex =
        sortedReferralsWithIndex[0].originalIndex;
      handleResponseSelectionMobile(firstReferralOriginalIndex);
    }
  }, [
    handleResponseSelectionMobile,
    selectedReferral,
    sortedReferralsWithIndex
  ]);

  const selectedResponseGroup = React.useMemo(() => {
    if (!viewOrDownloadDropdownItem?.length) return [];

    // Sort the items with isPrimaryContent first (aka hero attachment)
    return [...viewOrDownloadDropdownItem].sort((a, b) => {
      if (a.isPrimaryContent && !b.isPrimaryContent) return -1;
      if (!a.isPrimaryContent && b.isPrimaryContent) return 1;
      return 0;
    });
  }, [viewOrDownloadDropdownItem]);

  const handleNavigateToNext = React.useCallback(
    (currentDisplayIndex: number) => {
      const nextDisplayIndex = currentDisplayIndex + 1;
      if (nextDisplayIndex < sortedReferralsWithIndex.length) {
        const nextOriginalIndex =
          sortedReferralsWithIndex[nextDisplayIndex].originalIndex;
        handleResponseSelectionMobile(nextOriginalIndex);
      }
    },
    [sortedReferralsWithIndex, handleResponseSelectionMobile]
  );

  const handleNavigateToPrevious = React.useCallback(
    (currentDisplayIndex: number) => {
      const prevDisplayIndex = currentDisplayIndex - 1;
      if (prevDisplayIndex >= 0) {
        const prevOriginalIndex =
          sortedReferralsWithIndex[prevDisplayIndex].originalIndex;
        handleResponseSelectionMobile(prevOriginalIndex);
      }
    },
    [sortedReferralsWithIndex, handleResponseSelectionMobile]
  );

  return (
    <>
      <div className="mobile-view-main-container">
        <Tabs
          type="container"
          style={{ backgroundColor: '#edf5ff' }}
          className="mobile-get-plans-tab-container"
          selected={selectedDisplayIndex}
        >
          {sortedReferralsWithIndex?.length > 0 &&
            sortedReferralsWithIndex.map(
              (referralData: any, displayIndex: number) => {
                const { originalIndex, status, ...referral } = referralData;

                return (
                  <Tab
                    id={`tab-${referral.name}-${originalIndex}`}
                    key={`tab-${referral.name}-${originalIndex}`}
                    label={
                      <ReferralTabLabel referral={referral} status={status} />
                    }
                    className="mobile-get-plans-tab-trigger"
                    onClick={() => handleResponseSelectionMobile(originalIndex)}
                  >
                    {selectedDisplayIndex === displayIndex && (
                      <div className="mobile-view-tab-content-wrapper">
                        <ReferralDetails
                          referral={referral}
                          status={status}
                          onClickReferralContactButton={
                            handleShowReferralContactModal
                          }
                        />

                        {referral?.safetyMessage && (
                          <ReferralSafetyMessage
                            safetyMessage={referral?.safetyMessage || ''}
                          />
                        )}

                        {status === 'COMPLETED' && (
                          <ReferralResponseList
                            referralIndex={originalIndex}
                            isLoading={loadingEmailPreview}
                            responseGroups={referral?.responses}
                            initialSelectedResponse={selectedResponse}
                            selectedResponseGroup={selectedResponseGroup}
                            setShowResponseBodyModal={setShowResponseBodyModal}
                            setShowResponseWebMapModal={
                              setShowResponseWebMapModal
                            }
                            setShowResponsePdfModal={setShowResponsePdfModal}
                            setShowResponseImageModal={
                              setShowResponseImageModal
                            }
                            onClickResponsePreview={
                              handleViewOrDownloadDropdownChange
                            }
                            enquiryId={enquiryId}
                            emailPreview={emailPreview}
                            onResponseTabClick={handleMultipleResponseSelection}
                            onClickNextResponse={() =>
                              handleNavigateToNext(displayIndex)
                            }
                            isNextResponseDisabled={
                              displayIndex >=
                              sortedReferralsWithIndex.length - 1
                            }
                            onClickPreviousResponse={() =>
                              handleNavigateToPrevious(displayIndex)
                            }
                            isPreviousResponseDisabled={displayIndex === 0}
                            showPreviousButton={displayIndex > 0}
                            showNextButton={
                              displayIndex < sortedReferralsWithIndex.length - 1
                            }
                          />
                        )}
                        {status === 'WAITING' && (
                          <>
                            {referral?.notices &&
                              referral?.notices?.length > 0 && (
                                <ReferralAnnouncement
                                  notices={referral?.notices}
                                />
                              )}
                            <ReferralResponseNotReady
                              referral={referral}
                              onScrollPastEnd={() =>
                                handleNavigateToNext(displayIndex)
                              }
                              onScrollPastStart={() =>
                                handleNavigateToPrevious(displayIndex)
                              }
                              showPreviousOverscroll={displayIndex > 0}
                              showNextOverscroll={
                                displayIndex <
                                sortedReferralsWithIndex.length - 1
                              }
                            />
                          </>
                        )}
                        {status === 'EXPIRED' && (
                          <ReferralResponseExpired
                            onScrollPastEnd={() =>
                              handleNavigateToNext(displayIndex)
                            }
                            onScrollPastStart={() =>
                              handleNavigateToPrevious(displayIndex)
                            }
                            showPreviousOverscroll={displayIndex > 0}
                            showNextOverscroll={
                              displayIndex < sortedReferralsWithIndex.length - 1
                            }
                          />
                        )}

                        <ReferralContactModal
                          referral={referral}
                          showReferralContactModal={showReferralContactModal}
                          setShowReferralContactModal={
                            setShowReferralContactModal
                          }
                        />
                      </div>
                    )}
                  </Tab>
                );
              }
            )}
        </Tabs>
      </div>
      <div className="previewer-super-modal">
        {showResponseBodyModal && (
          <ResponseBodyModal
            previewData={previewData}
            referral={selectedReferralObj}
            enquiryId={enquiryId}
            inboundEmailId={inboundEmailIdToDownload}
            show={showResponseBodyModal}
            setShow={setShowResponseBodyModal}
          />
        )}
        {showResponseWebMapModal && (
          <ResponseWebMapModal
            previewData={previewData}
            referral={selectedReferralObj}
            show={showResponseWebMapModal}
            setShow={setShowResponseWebMapModal}
          />
        )}
        {showResponsePdfModal && (
          <ResponsePdfModal
            previewData={previewData}
            enquiryId={enquiryId}
            referral={selectedReferralObj}
            show={showResponsePdfModal}
            setShow={setShowResponsePdfModal}
          />
        )}
        {showResponseImageModal && (
          <ResponseImageModal
            previewData={previewData}
            enquiryId={enquiryId}
            referral={selectedReferralObj}
            show={showResponseImageModal}
            setShow={setShowResponseImageModal}
          />
        )}
      </div>
      <div className="mobile-view-modal-wrapper">
        {showResponsesExpiredModal && (
          <ResponsesExpiredModal
            showResponsesExpiredModal={showResponsesExpiredModal}
            onClickClose={onClickCloseResponseExpiredModal}
          />
        )}
      </div>
    </>
  );
};

export default MobileViewGetPlans;
